import { log } from '@/log'
import {
  DeliveryMethod as DeliveryMethodDto,
  DeliveryMethodDetail as DeliveryMethodDetailDto,
  PresetDeliveryMethod as PresetDeliveryMethodDto,
} from '@/models/api/delivery/deliveryMethod'
import { CartScopeEq } from '@/models/cart/group/scope'
import { DateWithoutHour } from '@/models/date-time/date-without-hour'
import { DeliveryMethod, DeliveryMethodDetail, PresetDeliveryMethod } from '@/models/delivery'
import {
  DeliveryMethodDetailParams,
  DeliveryMethodServiceInterface,
} from '@/services/api/delivery/deliveryMethod'
import _ from 'lodash'

export interface DeliveryMethodRepository {
  list(payment: string): Promise<DeliveryMethod[]>
  get(id: string, params: DeliveryMethodDetailParams): Promise<DeliveryMethodDetail>
  getPreset(payment: string): Promise<PresetDeliveryMethod>
}

export class DefaultDeliveryMethodRepository
  implements DeliveryMethodRepository {
  constructor(
    private readonly gateway: DeliveryMethodServiceInterface,
    private readonly cartScope: CartScopeEq
  ) {}

  async list(payment: string): Promise<DeliveryMethod[]> {
    const response = await this.gateway.list(this.cartScope, payment)

    return response.fold(
      (e) => {
        log.error(e)
        throw new Error(e.message)
      },
      (deliveryMethods) => {
        return _.map(deliveryMethods, convertDtoToDeliveryMethod)
      }
    )
  }

  async get(
    id: string,
    params: DeliveryMethodDetailParams
  ): Promise<DeliveryMethodDetail> {
    const response = await this.gateway.findById(id, params)

    return response.fold(
      (e) => {
        log.error(e)
        throw new Error(e.message)
      },
      (deliveryMethod) => {
        return convertDtoToDeliveryMethodDetail(deliveryMethod)
      }
    )
  }

  async getPreset(payment: string): Promise<PresetDeliveryMethod> {
    const response = await this.gateway.getPresetMethod(this.cartScope, payment)

    return response.fold(
      (e) => {
        log.error(e)
        throw new Error(e.message)
      },
      (deliveryMethod) => {
        return convertDtoToPresetDeliveryMethod(deliveryMethod)
      }
    )
  }
}

const convertDtoToDeliveryMethod = (dto: DeliveryMethodDto): DeliveryMethod => {
  return {
    id: dto.id,
    name: dto.name,
    externalId: dto.externalId,
    description: dto.description,
  }
}

const convertDtoToPresetDeliveryMethod = (dto: PresetDeliveryMethodDto): PresetDeliveryMethod => {
  return {
    ...convertDtoToDeliveryMethod(dto),
    unattendedDelivery: dto.unattendedDelivery,
  }
}

const convertDtoToDeliveryMethodDetail = (
  dto: DeliveryMethodDetailDto
): DeliveryMethodDetail => {
  return {
    id: dto.id,
    name: dto.name,
    externalId: dto.externalId,
    description: dto.description,
    deliveryDateTimes: _.map(dto.period, (dateDto) => {
      return {
        /**
         * 将来的に日付フォーマットを渡すようになった場合は、フォーマット情報をレスポンスに含めるか、決め打ちでserver側の知識にゲートウェイ層で依存する。
         * @see DateWithoutHour
         */
        date: DateWithoutHour.create(dateDto.date),
        timeList: dateDto.timeList,
        defaultTime: dateDto.defaultTime,
      }
    }),
    okihaiPlaces: dto.okihaiPlaceList,
  }
}
